import React from 'react';
import styled from 'styled-components';
import { colors } from '@/constants/colors';
import Btn from '../UI/Btn';
import { useUIStore } from '@/store/useUIStore';
import type { AdminDistributorApplication } from '@/api/admin';

interface Props {
   data: AdminDistributorApplication;
}

const REQUIRED_DOCS: Array<{
   key: keyof AdminDistributorApplication;
   label: string;
   required: boolean;
}> = [
   { key: 'govIdUrl', label: 'Valid government ID', required: true },
   { key: 'mayorsPermitUrl', label: "Mayor's / business permit", required: false },
   { key: 'bir2303Url', label: 'BIR 2303 Certificate of Registration', required: false },
   { key: 'proofOfAddressUrl', label: 'Proof of business address', required: false },
];

const QUALIFYING_QUESTIONS: Array<{
   key: keyof NonNullable<AdminDistributorApplication['qualifyingAnswers']>;
   label: string;
}> = [
   { key: 'hasPisoWifiExperience', label: 'Experience building piso WiFi' },
   { key: 'hasMikrotikKnowledge', label: 'MikroTik / RouterOS knowledge' },
   { key: 'isPriorDistributor', label: 'Previously a distributor of another piso WiFi' },
   { key: 'hasPhysicalLocation', label: 'Has physical store or office' },
   { key: 'willProvideSupport', label: 'Willing to provide technical support to buyers' },
   { key: 'hasWaitingCustomers', label: 'Has customers waiting to buy' },
   { key: 'willSellInOwnRegion', label: 'Will sell mainly in own region' },
   { key: 'hasOnlinePresence', label: 'Has Facebook page or online marketing presence' },
   { key: 'canCommitMonthly', label: 'Can commit to buying credits monthly' },
   { key: 'willDoOwnSetup', label: 'Will do the technical setup themselves' },
];

const isImageUrl = (url: string): boolean =>
   /\.(jpe?g|png|webp|gif|bmp|avif|heic)(\?|$)/i.test(url) ||
   /cloudinary\.com\/.*\/image\//i.test(url);

const ApplicationDocuments = ({ data }: Props) => {
   const { handleClose } = useUIStore();

   const applicantEmail =
      typeof data.applicant === 'object' ? data.applicant.email : '—';

   const uploadedCount = REQUIRED_DOCS.filter((d) => !!data[d.key]).length;
   const totalDocs = REQUIRED_DOCS.length;
   const missingRequired = REQUIRED_DOCS.some((d) => d.required && !data[d.key]);
   const complete = !missingRequired;

   const answers = data.qualifyingAnswers ?? null;
   const yesCount = answers
      ? QUALIFYING_QUESTIONS.filter((q) => !!answers[q.key]).length
      : 0;

   const uploadedDocsCount = [
      data.govIdUrl,
      data.mayorsPermitUrl,
      data.bir2303Url,
      data.proofOfAddressUrl,
   ].filter(Boolean).length;

   const handleView = (url: string) => {
      window.open(url, '_blank', 'noopener,noreferrer');
   };

   return (
      <Wrapper>
         <ApplicantHeader>
            <ApplicantName>{data.displayName}</ApplicantName>
            <ApplicantMeta>
               {data.businessName} · {data.city}, {data.region}
            </ApplicantMeta>
            <ApplicantContact>
               {applicantEmail}
               {data.contactPhone ? ` · ${data.contactPhone}` : ''}
            </ApplicantContact>
         </ApplicantHeader>

         <SummaryStrip>
            {data.bundleName && (
               <SummaryPill $variant="accent">
                  🎟️ {data.bundleName}
                  {data.bundleAmount != null
                     ? ` · ₱${data.bundleAmount.toLocaleString()}`
                     : ''}
               </SummaryPill>
            )}
            <SummaryPill $variant={data.paymentProofUrl ? 'green' : 'amber'}>
               {data.paymentProofUrl ? '✓ proof' : '⚠ no proof'}
            </SummaryPill>
            <SummaryPill>{uploadedDocsCount}/4 docs</SummaryPill>
            <SummaryPill>
               {yesCount}/{QUALIFYING_QUESTIONS.length} yes
            </SummaryPill>
         </SummaryStrip>

         {data.bundleName && (
            <>
               <SectionTitle>
                  <SectionTitleLbl>Starting Bundle &amp; Payment</SectionTitleLbl>
                  <CompletionPill $complete={!!data.paymentProofUrl}>
                     {data.paymentProofUrl ? '✓ Proof attached' : '⚠ No proof'}
                  </CompletionPill>
               </SectionTitle>
               <DetailsGrid>
                  <DetailCell>
                     <DetailLbl>Bundle</DetailLbl>
                     <DetailVal>
                        {data.bundleName}
                        {data.bundleCredits ? ` · ${data.bundleCredits} cr` : ''}
                     </DetailVal>
                  </DetailCell>
                  <DetailCell>
                     <DetailLbl>Amount paid</DetailLbl>
                     <DetailVal>
                        {data.bundleAmount != null
                           ? `₱${data.bundleAmount.toLocaleString()}`
                           : '—'}
                     </DetailVal>
                  </DetailCell>
                  <DetailCell>
                     <DetailLbl>Method</DetailLbl>
                     <DetailVal style={{ textTransform: 'uppercase' }}>
                        {data.paymentMethod === 'gcash'
                           ? 'GCash'
                           : data.paymentMethod === 'bank-transfer'
                             ? 'Bank'
                             : '—'}
                     </DetailVal>
                  </DetailCell>
               </DetailsGrid>
               {data.referenceNumber && (
                  <FbRow>
                     <FbLbl>Reference</FbLbl>
                     <DocFile title={data.referenceNumber}>
                        {data.referenceNumber}
                     </DocFile>
                  </FbRow>
               )}
               <DocList>
                  <DocRow $uploaded={!!data.paymentProofUrl}>
                     {data.paymentProofUrl && isImageUrl(data.paymentProofUrl) ? (
                        <DocThumb
                           src={data.paymentProofUrl}
                           alt="Payment proof"
                           onClick={() => handleView(data.paymentProofUrl!)}
                        />
                     ) : (
                        <DocIcon>{data.paymentProofUrl ? '✓' : '○'}</DocIcon>
                     )}
                     <DocBody>
                        <DocLabel>Payment screenshot</DocLabel>
                        {data.paymentProofUrl ? (
                           <DocMeta>
                              <DocFile title={data.paymentProofUrl}>
                                 {shortUrl(data.paymentProofUrl)}
                              </DocFile>
                           </DocMeta>
                        ) : (
                           <DocMissing>Not uploaded</DocMissing>
                        )}
                     </DocBody>
                     {data.paymentProofUrl && (
                        <DocActions>
                           <DocActionBtn onClick={() => handleView(data.paymentProofUrl!)}>
                              View
                           </DocActionBtn>
                        </DocActions>
                     )}
                  </DocRow>
               </DocList>
               <WarnBanner style={{ color: colors.GRAY3 }}>
                  Verify this payment against SmartFi's bank/GCash inbox before
                  approving. On approval, the bundle's credits are minted to the
                  distributor's wallet automatically.
               </WarnBanner>
            </>
         )}

         <DetailsGrid>
            <DetailCell>
               <DetailLbl>Default price per credit</DetailLbl>
               <DetailVal>₱{data.defaultPricePerCredit}</DetailVal>
            </DetailCell>
            <DetailCell>
               <DetailLbl>Submitted</DetailLbl>
               <DetailVal>
                  {new Date(data.createdAt).toLocaleDateString('en-PH', {
                     year: 'numeric',
                     month: 'short',
                     day: 'numeric',
                  })}
               </DetailVal>
            </DetailCell>
            <DetailCell>
               <DetailLbl>Status</DetailLbl>
               <DetailVal style={{ textTransform: 'uppercase' }}>{data.status}</DetailVal>
            </DetailCell>
         </DetailsGrid>

         {data.facebookUrl && (
            <FbRow>
               <FbLbl>Facebook</FbLbl>
               <FbLink href={data.facebookUrl} target="_blank" rel="noopener noreferrer">
                  {data.facebookUrl}
               </FbLink>
            </FbRow>
         )}

         <SectionTitle>
            <SectionTitleLbl>Qualifying Answers</SectionTitleLbl>
            <CompletionPill $complete={yesCount >= 5}>
               {yesCount}/{QUALIFYING_QUESTIONS.length} yes
            </CompletionPill>
         </SectionTitle>
         {answers ? (
            <AnswerList>
               {QUALIFYING_QUESTIONS.map((q) => {
                  const yes = !!answers[q.key];
                  return (
                     <AnswerRow key={String(q.key)} $yes={yes}>
                        <AnswerIcon $yes={yes}>{yes ? '✓' : '✗'}</AnswerIcon>
                        <AnswerLabel>{q.label}</AnswerLabel>
                     </AnswerRow>
                  );
               })}
            </AnswerList>
         ) : (
            <DocMissing>Qualifying answers not provided (legacy application).</DocMissing>
         )}

         {data.rejectionReason && (
            <NoteBox>
               <NoteLbl>Rejection reason</NoteLbl>
               <NoteText>"{data.rejectionReason}"</NoteText>
            </NoteBox>
         )}

         <SectionTitle>
            <SectionTitleLbl>Compliance Documents</SectionTitleLbl>
            <CompletionPill $complete={complete}>
               {complete
                  ? `✓ ${uploadedCount}/${totalDocs} provided`
                  : `⚠ Required doc missing`}
            </CompletionPill>
         </SectionTitle>

         <DocList>
            {REQUIRED_DOCS.map((req) => {
               const url = data[req.key] as string | undefined;
               const showThumb = !!url && isImageUrl(url);
               return (
                  <DocRow key={String(req.key)} $uploaded={!!url}>
                     {showThumb ? (
                        <DocThumb
                           src={url}
                           alt={req.label}
                           onClick={() => handleView(url!)}
                        />
                     ) : (
                        <DocIcon>{url ? '✓' : '○'}</DocIcon>
                     )}
                     <DocBody>
                        <DocLabel>
                           {req.label}
                           {!req.required && <OptionalTag>optional</OptionalTag>}
                        </DocLabel>
                        {url ? (
                           <DocMeta>
                              <DocFile title={url}>{shortUrl(url)}</DocFile>
                           </DocMeta>
                        ) : (
                           <DocMissing>Not uploaded</DocMissing>
                        )}
                     </DocBody>
                     {url && (
                        <DocActions>
                           <DocActionBtn onClick={() => handleView(url)}>
                              View
                           </DocActionBtn>
                        </DocActions>
                     )}
                  </DocRow>
               );
            })}
         </DocList>

         {!complete && (
            <WarnBanner>
               ⚠ This applicant is missing a required document (government ID).
            </WarnBanner>
         )}

         <BtnRow>
            <div onClick={handleClose}>
               <Btn
                  title="Close"
                  type="normal"
                  size="small"
                  bgcolor={colors.PRIMARY}
                  textcolor={colors.WHITE}
               />
            </div>
         </BtnRow>
      </Wrapper>
   );
};

function shortUrl(url: string): string {
   try {
      const u = new URL(url);
      const last = u.pathname.split('/').filter(Boolean).slice(-1)[0] || u.pathname;
      return last;
   } catch {
      return url;
   }
}

const Wrapper = styled.div`
   width: 560px;
   max-width: 100%;
   max-height: 80vh;
   overflow-y: auto;
   display: flex;
   flex-direction: column;
   gap: 14px;
   @media (max-width: 768px) { width: 100%; }
`;

const ApplicantHeader = styled.div`
   padding: 12px 14px;
   background: var(--app-brand-tint);
   border-radius: 10px;
   display: flex;
   flex-direction: column;
   gap: 2px;
`;

const ApplicantName = styled.p`
   font-size: 16px;
   font-weight: 800;
   color: ${colors.MAIN_DARK};
`;

const ApplicantMeta = styled.p`
   font-size: 12px;
   color: ${colors.GRAY3};
`;

const ApplicantContact = styled.p`
   font-size: 11px;
   color: ${colors.GRAY5};
   font-family: monospace;
   margin-top: 2px;
`;

const SummaryStrip = styled.div`
   display: flex;
   flex-wrap: wrap;
   gap: 6px;
   margin: 2px 0;
`;

const SummaryPill = styled.span<{ $variant?: 'accent' | 'green' | 'amber' }>`
   font-size: 11px;
   font-weight: 700;
   padding: 4px 10px;
   border-radius: 999px;
   white-space: nowrap;
   background: ${({ $variant }) =>
      $variant === 'accent'
         ? `${colors.PRIMARY}18`
         : $variant === 'green'
           ? `${colors.SUCCESS}18`
           : $variant === 'amber'
             ? `${colors.WARNING}18`
             : colors.GRAY14};
   color: ${({ $variant }) =>
      $variant === 'accent'
         ? colors.PRIMARY
         : $variant === 'green'
           ? colors.SUCCESS
           : $variant === 'amber'
             ? colors.WARNING
             : colors.GRAY3};
`;

const DetailsGrid = styled.div`
   display: grid;
   grid-template-columns: repeat(3, 1fr);
   gap: 8px;
   @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const DetailCell = styled.div`
   padding: 8px 12px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
`;

const DetailLbl = styled.p`
   font-size: 10px;
   font-weight: 700;
   color: ${colors.GRAY5};
   text-transform: uppercase;
   letter-spacing: 0.05em;
`;

const DetailVal = styled.p`
   font-size: 13px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
   margin-top: 2px;
`;

const NoteBox = styled.div`
   padding: 10px 12px;
   background: ${colors.WARNING}10;
   border-left: 3px solid ${colors.WARNING};
   border-radius: 4px;
`;

const NoteLbl = styled.p`
   font-size: 10px;
   font-weight: 700;
   color: ${colors.WARNING};
   text-transform: uppercase;
   letter-spacing: 0.05em;
   margin-bottom: 4px;
`;

const NoteText = styled.p`
   font-size: 12px;
   color: ${colors.GRAY3};
   font-style: italic;
   line-height: 1.5;
`;

const SectionTitle = styled.div`
   display: flex;
   align-items: center;
   justify-content: space-between;
   padding-top: 10px;
   border-top: 1px solid ${colors.GRAY13};
   margin-top: 4px;
`;

const SectionTitleLbl = styled.h4`
   font-size: 12px;
   font-weight: 800;
   color: ${colors.GRAY3};
   text-transform: uppercase;
   letter-spacing: 0.05em;
`;

const CompletionPill = styled.span<{ $complete: boolean }>`
   font-size: 11px;
   font-weight: 700;
   padding: 3px 10px;
   border-radius: 10px;
   background: ${({ $complete }) => ($complete ? `${colors.SUCCESS}18` : `${colors.WARNING}18`)};
   color: ${({ $complete }) => ($complete ? colors.SUCCESS : colors.WARNING)};
`;

const DocList = styled.div`
   display: flex;
   flex-direction: column;
   gap: 6px;
`;

const DocRow = styled.div<{ $uploaded: boolean }>`
   display: flex;
   align-items: center;
   gap: 10px;
   padding: 10px 12px;
   border: 1px solid
      ${({ $uploaded }) => ($uploaded ? `${colors.SUCCESS}30` : `${colors.WARNING}30`)};
   background: ${({ $uploaded }) =>
      $uploaded ? colors.WHITE : `${colors.WARNING}06`};
   border-radius: 8px;
`;

const DocIcon = styled.span`
   font-size: 16px;
   font-weight: 900;
   color: ${colors.SUCCESS};
   width: 20px;
   text-align: center;
   flex-shrink: 0;
`;

const DocBody = styled.div`
   flex: 1;
   min-width: 0;
`;

const DocLabel = styled.p`
   font-size: 13px;
   font-weight: 700;
   color: ${colors.MAIN_DARK};
`;

const DocMeta = styled.div`
   display: flex;
   gap: 8px;
   align-items: center;
   flex-wrap: wrap;
   margin-top: 2px;
`;

const DocFile = styled.span`
   font-size: 11px;
   color: ${colors.GRAY3};
   font-family: monospace;
   word-break: break-all;
`;

const DocMissing = styled.p`
   font-size: 11px;
   color: ${colors.WARNING};
   font-weight: 600;
   font-style: italic;
`;

const DocActions = styled.div`
   display: flex;
   gap: 4px;
   flex-shrink: 0;
`;

const DocThumb = styled.img`
   width: 44px;
   height: 44px;
   object-fit: cover;
   border-radius: 6px;
   border: 1px solid ${colors.GRAY12};
   flex-shrink: 0;
   cursor: zoom-in;
   background: ${colors.GRAY14};
`;

const OptionalTag = styled.span`
   display: inline-block;
   margin-left: 6px;
   font-size: 9px;
   font-weight: 700;
   padding: 1px 6px;
   border-radius: 999px;
   background: ${colors.GRAY13};
   color: ${colors.GRAY5};
   text-transform: uppercase;
   letter-spacing: 0.04em;
   vertical-align: middle;
`;

const FbRow = styled.div`
   display: flex;
   gap: 10px;
   align-items: center;
   padding: 8px 12px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   flex-wrap: wrap;
`;

const FbLbl = styled.span`
   font-size: 10px;
   font-weight: 700;
   color: ${colors.GRAY5};
   text-transform: uppercase;
   letter-spacing: 0.05em;
`;

const FbLink = styled.a`
   font-size: 12px;
   color: ${colors.PRIMARY};
   text-decoration: none;
   word-break: break-all;
   &:hover { text-decoration: underline; }
`;

const AnswerList = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 6px;
   @media (max-width: 480px) { grid-template-columns: 1fr; }
`;

const AnswerRow = styled.div<{ $yes: boolean }>`
   display: flex;
   align-items: flex-start;
   gap: 8px;
   padding: 8px 10px;
   border: 1px solid
      ${({ $yes }) => ($yes ? `${colors.SUCCESS}30` : colors.GRAY12)};
   background: ${({ $yes }) => ($yes ? `${colors.SUCCESS}08` : 'transparent')};
   border-radius: 6px;
`;

const AnswerIcon = styled.span<{ $yes: boolean }>`
   font-size: 13px;
   font-weight: 900;
   color: ${({ $yes }) => ($yes ? colors.SUCCESS : colors.GRAY5)};
   flex-shrink: 0;
   width: 14px;
   text-align: center;
`;

const AnswerLabel = styled.span`
   font-size: 11px;
   color: ${colors.MAIN_DARK};
   line-height: 1.4;
`;

const DocActionBtn = styled.button`
   background: ${colors.GRAY14};
   border: 1px solid ${colors.GRAY12};
   padding: 4px 10px;
   border-radius: 5px;
   font-size: 11px;
   font-weight: 600;
   color: ${colors.GRAY1};
   cursor: pointer;
   &:hover { background: ${colors.GRAY13}; }
`;

const WarnBanner = styled.p`
   font-size: 12px;
   color: ${colors.WARNING};
   padding: 10px 12px;
   background: ${colors.WARNING}10;
   border: 1px solid ${colors.WARNING}30;
   border-radius: 8px;
   line-height: 1.5;
`;

const BtnRow = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 10px;
   padding-top: 6px;
`;

export default ApplicationDocuments;
